import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Mail, Check, Zap, Star, Gem, Clock, Shield, Award, Users, Sparkles, TrendingUp, ArrowRight, TrendingDown, Smartphone, MapPin, ShieldAlert, UserX, BadgeCheck, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { portfolioItems, features, offers, trustText, contactInfo } from '../data/mockData';

const Navigation = () => {
  const navItems = [
    { label: 'Pourquoi avoir un site', href: '#pourquoi-avoir-site' },
    { label: 'Modèles', href: '#modeles' },
    { label: 'Mes offres', href: '#offres' },
    { label: 'Comment ça marche', href: '#comment-ca-marche' },
    { label: 'Travailler avec moi', href: '#travailler-avec-moi' },
    { label: 'Ma Garantie', href: '#garantie' },
    { label: 'Mes Valeurs', href: '#valeurs' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80; // Height of sticky nav
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className="hidden lg:block fixed top-0 left-0 right-0 z-50 bg-primary-dark/95 backdrop-blur-md border-b border-border-dark">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="text-2xl font-bold text-gold">
            Sensai Yang
          </a>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="text-gray-300 hover:text-gold transition-colors duration-300 text-sm font-medium"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const featureIcons = {
    0: Clock,
    1: Shield,
    2: Award,
    3: Users,
    4: Sparkles,
    5: TrendingUp
  };

  const offerIcons = {
    zap: Zap,
    star: Star,
    gem: Gem
  };

  return (
    <div className="min-h-screen bg-primary-dark text-white">
      {/* Navigation */}
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden lg:pt-20">
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="absolute inset-0 hero-pattern opacity-10"></div>
        
        <div className={`container mx-auto px-6 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-4xl mx-auto text-center pt-6 sm:pt-0">
            <div className="flex items-center justify-center gap-2 mb-6">
              <BadgeCheck className="h-5 w-5 text-gold" />
              <span className="text-gold font-semibold text-lg">Déjà +50 sites créés</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block text-gold">Des sites professionnels</span>
              <span className="block">qui fidélisent vos nouveaux clients.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Création de sites vitrines pour indépendants, artisans et professionnels
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center text-lg text-gray-400 mb-12 font-light">
              <p>À partir de 350€ — Sans abonnement — Livraison 48h</p>
              <span className="hidden sm:inline text-gold">•</span>
              <p className="text-gold font-semibold">4 sites déjà livrés en 2026</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-gold hover:bg-gold-dark text-primary-dark font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105 hover:shadow-gold"
                asChild
              >
                <a href={`tel:${contactInfo.phoneLink}`}>
                  <Phone className="mr-2 h-5 w-5" />
                  Appelez-moi maintenant
                </a>
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto border-2 border-blue-elegant hover:bg-blue-elegant/10 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
                asChild
              >
                <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp
                </a>
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto border-2 border-red-500 hover:bg-red-500/10 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
                asChild
              >
                <a href={`mailto:${contactInfo.email}`}>
                  <Mail className="mr-2 h-5 w-5" />
                  Me contacter
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section - Pourquoi avoir un site */}
      <section id="pourquoi-avoir-site" className="py-24 bg-primary-dark relative scroll-mt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-dark/50 to-primary-dark"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-12 max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Aujourd'hui, ne pas avoir de site vous <span className="text-gold">coûte des clients</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Même avec une fiche Google optimisée, vous perdez des opportunités chaque jour.<br />
              En 2026, avoir un site internet n'est plus une option. C'est un standard.<br />
              <span className="text-gold font-semibold">C'est votre nouvelle carte de visite professionnelle.</span>
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
            <Card className="bg-card-dark border-border-dark hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mb-4">
                  <UserX className="h-6 w-6 text-gold" />
                </div>
                <CardTitle className="text-white text-xl mb-2">Vous ne contrôlez pas votre image</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Une fiche Google est limitée. Un site vous permet de présenter votre entreprise comme vous le souhaitez et de lui montrer votre identité personnelle.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mb-4">
                  <TrendingDown className="h-6 w-6 text-gold" />
                </div>
                <CardTitle className="text-white text-xl mb-2">Vous perdez des clients face à vos concurrents</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Si votre concurrent a un site et pas vous, le choix est vite fait pour le client.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mb-4">
                  <Smartphone className="h-6 w-6 text-gold" />
                </div>
                <CardTitle className="text-white text-xl mb-2">Les clients vérifient avant d'appeler</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  La majorité des prospects consultent un site avant de prendre contact. Sans site, ils partent ailleurs voir si vos concurrents ont eux, un site.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mb-4">
                  <MessageCircle className="h-6 w-6 text-gold" />
                </div>
                <CardTitle className="text-white text-xl mb-2">Aucun moyen d'orienter le client vers l'action</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Un site guide naturellement vers :<br />
                  • Appel direct<br />
                  • WhatsApp<br />
                  • Prise de rendez-vous
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="h-6 w-6 text-gold" />
                </div>
                <CardTitle className="text-white text-xl mb-2">Référencement local renforcé</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Un site connecté à votre fiche Google améliore votre visibilité locale, votre crédibilité et donc la position de votre fiche Google.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mb-4">
                  <ShieldAlert className="h-6 w-6 text-gold" />
                </div>
                <CardTitle className="text-white text-xl mb-2">Vous êtes dépendant d'une seule plateforme</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Si Google change ses règles ou suspend votre fiche, vous perdez tout. C'est un levier commercial.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center px-4">
            <Button 
              size="lg" 
              className="bg-gold hover:bg-gold-dark text-primary-dark font-semibold px-4 sm:px-10 py-4 sm:py-6 text-xs sm:text-lg transition-all duration-300 hover:scale-105 hover:shadow-gold max-w-full"
              asChild
            >
              <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center text-center leading-tight gap-2 whitespace-normal">
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:block">J'obtiens mon site dans les prochaines 48 heures</span>
                <span className="block sm:hidden">Mon site en 48h</span>
              </a>
            </Button>
          </div>
        </div>
      </section>


      {/* Modèles de présentation */}
      <section id="modeles" className="py-24 bg-primary-dark scroll-mt-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Modèles de <span className="text-gold">présentation</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Exemples de structures que je crée pour mes clients
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {portfolioItems.map((item) => (
              <Card key={item.id} className="bg-card-dark border-border-dark overflow-hidden group hover:border-blue-elegant/50 transition-all duration-300 flex flex-col">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-white">{item.name}</CardTitle>
                  <Badge variant="outline" className="w-fit border-gold/30 text-gold">
                    {item.category}
                  </Badge>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col justify-between">
                  <CardDescription className="text-gray-400 mb-4">
                    {item.description}
                  </CardDescription>
                  <Button 
                    variant="outline"
                    className="w-full border-gold/30 text-gold hover:bg-gold/10 hover:text-white transition-all duration-300"
                    asChild
                  >
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      Découvrez le modèle en cliquant ici
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Les Offres */}
      <section id="offres" className="py-24 bg-secondary-dark relative scroll-mt-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Les <span className="text-gold">Offres</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Choisissez la formule adaptée à vos besoins
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {offers.map((offer) => {
              const Icon = offerIcons[offer.icon];
              
              // Définir les styles de fond pour chaque offre
              let bgStyle = '';
              let borderStyle = '';
              let buttonStyle = '';
              
              if (offer.id === 1) { // Offre Essentielle
                bgStyle = 'bg-gradient-to-br from-gold/10 to-gold/5';
                borderStyle = 'border-gold/30 hover:border-gold/50';
                buttonStyle = 'bg-gold hover:bg-gold-dark text-primary-dark';
              } else if (offer.id === 2) { // Offre Professionnelle
                bgStyle = 'bg-gradient-to-br from-blue-elegant/20 to-blue-elegant/10';
                borderStyle = 'border-gold shadow-gold-lg ring-2 ring-gold/20';
                buttonStyle = 'bg-gold hover:bg-gold-dark text-primary-dark';
              } else if (offer.id === 3) { // Offre Prestige
                bgStyle = 'bg-gradient-to-br from-purple-600/15 to-purple-900/10';
                borderStyle = 'border-purple-500/30 hover:border-purple-500/50';
                buttonStyle = 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white';
              }
              
              return (
                <Card 
                  key={offer.id} 
                  className={`${bgStyle} ${borderStyle} border-2 transition-all duration-300 hover:transform hover:scale-105`}
                >
                  <CardHeader>
                    {offer.popular && (
                      <Badge className="w-fit bg-gold text-primary-dark mb-2">
                        Populaire
                      </Badge>
                    )}
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-elegant/20 to-gold/20 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-gold" />
                    </div>
                    <CardTitle className="text-2xl text-white">{offer.name}</CardTitle>
                    <div className="text-4xl font-bold text-gold mt-2">{offer.price}</div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-4">
                      {offer.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <Check className="h-5 w-5 text-gold mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="flex items-center gap-2 mb-6 text-gray-400 italic text-sm">
                      <ArrowRight className="h-4 w-4 text-gold flex-shrink-0" />
                      <span>{offer.description}</span>
                    </div>
                    
                    <Button 
                      className={`w-full ${buttonStyle} transition-all duration-300`}
                      asChild
                    >
                      <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                        Me contacter
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comment ça marche ? */}
      <section id="comment-ca-marche" className="py-24 bg-secondary-dark relative scroll-mt-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Comment ça <span className="text-gold">marche</span> ?
            </h2>
            <p className="text-gray-400 text-lg">
              Un processus simple et transparent en 3 étapes
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="bg-card-dark border-border-dark text-center hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-gold">1</span>
                </div>
                <CardTitle className="text-white text-xl">Vous me contactez</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Par téléphone, WhatsApp ou email. Vous m'expliquez votre projet et vos besoins.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark text-center hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-gold">2</span>
                </div>
                <CardTitle className="text-white text-xl">Je crée votre site en 48h</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Je conçois et développe votre site professionnel adapté à votre activité.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark text-center hover:border-gold/30 transition-all duration-300">
              <CardHeader>
                <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-gold">3</span>
                </div>
                <CardTitle className="text-white text-xl">Mise en ligne + accès complet</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  Votre site est en ligne. Vous recevez tous les accès. Il vous appartient.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pourquoi travailler avec moi */}
      <section id="travailler-avec-moi" className="py-24 bg-secondary-dark relative scroll-mt-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Pourquoi travailler <span className="text-gold">avec moi</span> ?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Une approche simple, efficace et transparente pour votre présence en ligne
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const Icon = featureIcons[index];
              return (
                <Card key={index} className="bg-card-dark border-border-dark hover:border-gold/50 transition-all duration-300 hover:transform hover:scale-105 card-glow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-gold" />
                    </div>
                    <CardTitle className="text-white text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Garantie 48h */}
      <section id="garantie" className="py-24 bg-secondary-dark relative scroll-mt-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-12 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <ShieldCheck className="h-10 w-10 text-gold" />
              <h2 className="text-4xl md:text-5xl font-bold">
                Garantie 48h — <span className="text-gold">Engagement total</span>
              </h2>
            </div>
            <p className="text-xl text-gray-300 mb-8">
              Un délai clair. Une promesse forte. Zéro risque pour vous.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-br from-blue-elegant/10 to-gold/10 border-gold/30 backdrop-blur-sm mb-8">
              <CardContent className="text-center py-8">
                <p className="text-xl md:text-2xl text-white leading-relaxed font-medium mb-4">
                  Livraison garantie sous 48h à partir de la réception de tous les éléments nécessaires et signature du contrat.
                </p>
                <p className="text-lg md:text-xl text-gold font-semibold">
                  Si ce délai est dépassé de mon fait, vous êtes intégralement remboursé et vous conservez votre site.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-card-dark border-border-dark">
              <CardHeader>
                <CardTitle className="text-white text-center text-xl">
                  Le délai de 48h démarre uniquement lorsque les éléments suivants sont reçus :
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">Logo reçu</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">Textes validés</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">Photos validées</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">Coordonnées exactes</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">Signature OK</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold mr-3 flex-shrink-0" />
                    <span className="text-gray-300">Paiement intégral reçu</span>
                  </li>
                </ul>
                <p className="text-center text-sm text-gray-400 italic">
                  Process simple, clair et structuré pour garantir rapidité et qualité.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section Confiance */}
      <section id="valeurs" className="py-24 bg-primary-dark scroll-mt-20">
        <div className="container mx-auto px-6">
          <Card className="max-w-4xl mx-auto bg-gradient-to-br from-blue-elegant/10 to-gold/10 border-gold/30 backdrop-blur-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl md:text-4xl font-bold text-white mb-4">
                {trustText.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                {trustText.content}
              </p>
              <Button 
                size="lg" 
                className="bg-gold hover:bg-gold-dark text-primary-dark font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105 hover:shadow-gold"
                asChild
              >
                <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Contactez-moi ICI
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-secondary-dark border-t border-border-dark">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-gold mb-2">Sensai Yang</h3>
              <p className="text-gray-400">Création de sites internet professionnels</p>
            </div>
            
            <div className="flex flex-col items-center md:items-end gap-3">
              <a 
                href={`tel:${contactInfo.phoneLink}`} 
                className="text-lg hover:text-gold transition-colors flex items-center gap-2"
              >
                <Phone className="h-5 w-5" />
                {contactInfo.phone}
              </a>
              <a 
                href={`mailto:${contactInfo.email}`} 
                className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2"
              >
                <Mail className="h-5 w-5" />
                {contactInfo.email}
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-border-dark text-center text-gray-500">
            <p>&copy; 2026 Sensai Yang. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href={contactInfo.whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float-btn fixed bottom-6 right-6 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-2xl transition-colors duration-300 z-50"
        aria-label="Contact WhatsApp"
      >
        <MessageCircle className="h-8 w-8 text-white" />
      </a>
    </div>
  );
};

export default Home;
